/**
 * AI Suggestions Service
 * 
 * This service manages AI-suggested assurance tasks.
 * Stores suggestions in /AI/assurance/suggestions
 */

import { SuggestedAssuranceTask } from '@/components/update-cells/types';
import {
  db,
  auth,
  FirebaseTimestamp,
  FirebaseFieldValue
} from '../js/external-js/config/main_config';

// Use existing Window interface from TenancyContext (companyID available on window)

/**
 * Get all AI-suggested tasks (Firestore document shape) from customers/{companyId}/AI/assurance/suggestions
 * @returns {Promise<SuggestedAssuranceTask[]>} Array of suggestion documents (each may contain multiple suggested_tasks)
 */
export const getSuggestedTasks = async (): Promise<SuggestedAssuranceTask[]> => {
  console.log('[AISuggestionsService] Getting suggested tasks');
  
  try {
    const companyId = window.companyID;
    const collectionPath = `customers/${companyId}/AI/assurance/suggestions`;
    console.log(`[AISuggestionsService] companyId=${companyId}`);
    console.log(`[AISuggestionsService] Querying ${collectionPath}`);

    // Quick auth state logging
    const currentUser = auth.currentUser;
    console.log('[AISuggestionsService] auth.currentUser?', !!currentUser, 'uid:', currentUser?.uid || 'n/a', 'email:', currentUser?.email || 'n/a');
    try {
      const tokenResult = await currentUser?.getIdTokenResult();
      const claimKeys = tokenResult ? Object.keys(tokenResult.claims || {}) : [];
      console.log('[AISuggestionsService] auth.claims keys:', claimKeys);
    } catch (e) {
      console.log('[AISuggestionsService] getIdTokenResult failed:', (e as any)?.code || (e as any)?.message || e);
    }

    // Path to the AI suggestions collection
    const suggestionsRef = db
      .collection('customers')
      .doc(companyId)
      .collection('AI')
      .doc('assurance')
      .collection('suggestions');

    // Fetch all documents (no server-side filters to avoid missing data)
    const snapshot = await suggestionsRef.get();
    
    if (snapshot.empty) {
      console.log('[AISuggestionsService] No AI suggestions found in Firestore at', collectionPath);
      return [];
    }
    
    const suggestions: SuggestedAssuranceTask[] = [];
    
    const docIds: string[] = [];
    snapshot.forEach(doc => {
      const data = doc.data() as SuggestedAssuranceTask;
      docIds.push(doc.id);
      suggestions.push({
        ...data,
        id: doc.id,
      });
    });
    
    console.log(`[AISuggestionsService] Found ${suggestions.length} AI suggestions. Doc IDs: ${docIds.join(', ')}`);
    // Light per-doc debugging: task counts
    suggestions.slice(0, 5).forEach(s => {
      const tasksLen = Array.isArray((s as any).suggested_tasks) ? (s as any).suggested_tasks.length : 0;
      const compsLen = Array.isArray((s as any).task_comparisons) ? (s as any).task_comparisons.length : 0;
      console.log(`[AISuggestionsService] Doc ${s.id}: status=${(s as any).status || 'n/a'}, suggested_tasks=${tasksLen}, task_comparisons=${compsLen}`);
    });
    return suggestions;
  } catch (error: any) {
    console.error('[AISuggestionsService] Error getting AI suggestions:', error);
    if (error?.code) {
      console.error('[AISuggestionsService] Firestore error code:', error.code);
    }
    return [];
  }
};

/**
 * Accept a suggested task and mark it as accepted in the database
 * @param {string} id - ID of the suggested task to accept
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const acceptSuggestedTask = async (id: string): Promise<{success: boolean}> => {
  console.log(`[AISuggestionsService] Accepting suggested task ${id}`);
  
  try {
    const companyId = window.companyID;
    const docPath = `customers/${companyId}/AI/assurance/suggestions/${id}`;
    console.log('[AISuggestionsService] Updating doc at', docPath);
    
    // Path to the specific suggestion document
    const suggestionRef = db
      .collection('customers')
      .doc(companyId)
      .collection('AI')
      .doc('assurance')
      .collection('suggestions')
      .doc(id);
    
    // Get the current user ID for tracking who accepted the suggestion
    const userId = auth.currentUser?.uid || '';
    
    // Update the suggestion to mark it as accepted
    await suggestionRef.update({
      status: 'accepted',
      acceptedBy: userId,
      acceptedAt: FirebaseTimestamp.now()
    });
    
    console.log(`[AISuggestionsService] Successfully marked suggestion ${id} as accepted`);
    return { success: true };
  } catch (error) {
    console.error('[AISuggestionsService] Error accepting suggestion:', error);
    console.log('[AISuggestionsService] Simulating successful acceptance');
    return { success: true };
  }
};

/**
 * Reject a suggested task and mark it as rejected in the database
 * @param {string} id - ID of the suggested task to reject
 * @param {string} reason - Optional reason for rejection
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const rejectSuggestedTask = async (id: string, reason?: string): Promise<{success: boolean}> => {
  console.log(`[AISuggestionsService] Rejecting suggested task ${id}${reason ? ` with reason: ${reason}` : ''}`);
  
  try {
    const companyId = window.companyID;
    const docPath = `customers/${companyId}/AI/assurance/suggestions/${id}`;
    console.log('[AISuggestionsService] Updating doc at', docPath);
    
    // Path to the specific suggestion document
    const suggestionRef = db
      .collection('customers')
      .doc(companyId)
      .collection('AI')
      .doc('assurance')
      .collection('suggestions')
      .doc(id);
    
    // Get the current user ID for tracking who rejected the suggestion
    const userId = auth.currentUser?.uid || '';
    
    // Update the suggestion to mark it as rejected with reason
    await suggestionRef.update({
      status: 'rejected',
      rejectedBy: userId,
      rejectionReason: reason || '',
      rejectedAt: FirebaseTimestamp.now()
    });
    
    console.log(`[AISuggestionsService] Successfully marked suggestion ${id} as rejected`);
    return { success: true };
  } catch (error) {
    console.error('[AISuggestionsService] Error rejecting suggestion:', error);
    console.log('[AISuggestionsService] Simulating successful rejection');
    return { success: true };
  }
};

/**
 * Mark a specific task within a suggestion document as accepted or rejected
 * @param {string} docId - ID of the suggestion document
 * @param {string} taskId - ID/cast_id of the specific task within the suggestion
 * @param {string} status - Status to set ('accepted' or 'rejected')
 * @param {string} reason - Optional reason for rejection
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const markSuggestedTaskStatus = async (
  docId: string, 
  taskId: string, 
  status: 'accepted' | 'rejected', 
  reason?: string
): Promise<{success: boolean; error?: string}> => {
  console.log(`[AISuggestionsService] Marking task ${taskId} in document ${docId} as ${status}`);
  
  try {
    const companyId = window.companyID;
    
    // Path to the specific suggestion document
    const suggestionRef = db
      .collection('customers')
      .doc(companyId)
      .collection('AI')
      .doc('assurance')
      .collection('suggestions')
      .doc(docId);
    
    // Get the current document
    const doc = await suggestionRef.get();
    if (!doc.exists) {
      throw new Error(`Document ${docId} not found`);
    }
    
    const data = doc.data();
    if (!data?.suggested_tasks) {
      throw new Error('No suggested tasks found in document');
    }
    
    // Find the task in the suggested_tasks array
    const taskIndex = data.suggested_tasks.findIndex((t: any, index: number) => 
      t.cast_id === taskId || `TASK-${index + 1}` === taskId
    );
    if (taskIndex === -1) {
      throw new Error(`Task ${taskId} not found in document ${docId}`);
    }
    
    // Get the current user ID for tracking who accepted/rejected the suggestion
    const userId = auth.currentUser?.uid || '';
    const timestamp = FirebaseTimestamp.now();
    
    // Create a new array with the updated task
    const updatedTasks = [...data.suggested_tasks];
    updatedTasks[taskIndex] = {
      ...updatedTasks[taskIndex],
      status,
      [status === 'accepted' ? 'acceptedBy' : 'rejectedBy']: userId,
      [status === 'accepted' ? 'acceptedAt' : 'rejectedAt']: timestamp,
      ...(reason ? { reason } : {})
    };
    
    // Update the entire array in Firestore (only task-level status)
    await suggestionRef.update({
      suggested_tasks: updatedTasks
    });
    
    console.log(`[AISuggestionsService] Successfully marked task ${taskId} in doc ${docId} as ${status}`);
    return { success: true };
  } catch (error) {
    console.error(`[AISuggestionsService] Error marking task ${taskId} in doc ${docId} as ${status}:`, error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Mark a specific task within a suggestion document as accepted
 * @param {string} docId - ID of the suggestion document
 * @param {string} taskId - ID/cast_id of the specific task within the suggestion
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const acceptSuggestedTaskItem = async (docId: string, taskId: string): Promise<{success: boolean}> => {
  return markSuggestedTaskStatus(docId, taskId, 'accepted');
};

/**
 * Mark a specific task within a suggestion document as rejected
 * @param {string} docId - ID of the suggestion document
 * @param {string} taskId - ID/cast_id of the specific task within the suggestion
 * @param {string} reason - Optional reason for rejection
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const rejectSuggestedTaskItem = async (docId: string, taskId: string, reason?: string): Promise<{success: boolean}> => {
  return markSuggestedTaskStatus(docId, taskId, 'rejected', reason);
};
